import { api, toCamelCase, toSnakeCase } from '../lib/api'
import {
  IncomingInvoice,
  IncomingInvoiceCreate,
  IncomingInvoiceFile,
  IncomingInvoiceStatus,
  IncomingInvoiceUpdate,
} from '../types/incomingInvoice'

export interface GetIncomingInvoicesParams {
  status?: IncomingInvoiceStatus
  search?: string
  dateFrom?: string
  dateTo?: string
  includeArchived?: boolean
  skip?: number
  limit?: number
}

function parseIncomingInvoice(data: Record<string, unknown>): IncomingInvoice {
  const invoice = toCamelCase(data) as unknown as IncomingInvoice
  invoice.totalAmount = Number(invoice.totalAmount) || 0
  if (invoice.files) {
    invoice.files = invoice.files.map((f: any) =>
      typeof f === 'object' && f !== null ? (toCamelCase(f as Record<string, unknown>) as unknown as IncomingInvoiceFile) : f
    )
  } else {
    invoice.files = []
  }
  return invoice
}

export const incomingInvoiceApi = {
  async getAll(params?: GetIncomingInvoicesParams): Promise<IncomingInvoice[]> {
    const queryParams: Record<string, string | number | boolean | undefined> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.search) queryParams.search = params.search
    if (params?.dateFrom) queryParams.date_from = params.dateFrom
    if (params?.dateTo) queryParams.date_to = params.dateTo
    if (params?.includeArchived) queryParams.include_archived = params.includeArchived
    if (params?.skip !== undefined) queryParams.skip = params.skip
    if (params?.limit !== undefined) queryParams.limit = params.limit

    const response = await api.get('/incoming-invoices', { params: queryParams })
    return response.data.map((inv: unknown) =>
      parseIncomingInvoice(inv as Record<string, unknown>)
    )
  },

  async getById(id: number): Promise<IncomingInvoice> {
    const response = await api.get(`/incoming-invoices/${id}`)
    return parseIncomingInvoice(response.data as Record<string, unknown>)
  },

  async create(data: IncomingInvoiceCreate): Promise<IncomingInvoice> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.post('/incoming-invoices', payload)
    return parseIncomingInvoice(response.data as Record<string, unknown>)
  },

  async update(id: number, data: IncomingInvoiceUpdate): Promise<IncomingInvoice> {
    const payload = toSnakeCase(data as unknown as Record<string, unknown>)
    const response = await api.put(`/incoming-invoices/${id}`, payload)
    return parseIncomingInvoice(response.data as Record<string, unknown>)
  },

  async updateStatus(id: number, status: IncomingInvoiceStatus): Promise<IncomingInvoice> {
    const response = await api.patch(`/incoming-invoices/${id}/status`, { status })
    return parseIncomingInvoice(response.data as Record<string, unknown>)
  },

  async annul(id: number): Promise<IncomingInvoice> {
    const response = await api.post(`/incoming-invoices/${id}/annul`)
    return parseIncomingInvoice(response.data as Record<string, unknown>)
  },

  async uploadFile(invoiceId: number, file: File): Promise<IncomingInvoiceFile> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post(`/incoming-invoices/${invoiceId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return toCamelCase(response.data as Record<string, unknown>) as unknown as IncomingInvoiceFile
  },

  async deleteFile(invoiceId: number, fileId: number): Promise<void> {
    await api.delete(`/incoming-invoices/${invoiceId}/files/${fileId}`)
  },

  getFileDownloadUrl(invoiceId: number, fileId: number): string {
    return `${api.defaults.baseURL}/incoming-invoices/${invoiceId}/files/${fileId}/download`
  },

  async exportCsv(params?: Omit<GetIncomingInvoicesParams, 'skip' | 'limit'>): Promise<Blob> {
    const queryParams: Record<string, string | boolean | undefined> = {}
    if (params?.status) queryParams.status = params.status
    if (params?.dateFrom) queryParams.date_from = params.dateFrom
    if (params?.dateTo) queryParams.date_to = params.dateTo
    if (params?.includeArchived) queryParams.include_archived = params.includeArchived

    const response = await api.get('/incoming-invoices/export', {
      params: queryParams,
      responseType: 'blob',
    })
    return response.data
  },
}
